// src/components/TeacherMessagesBox.js
import React, { useEffect, useState, useMemo } from 'react';
import {
  Typography,
  Box,
  Paper,
  Stack,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
  Link as MuiLink,
  Snackbar,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import emptyBox from '../img/EmptyBox.png';
import { useTranslation } from "react-i18next";
import { getAuth } from 'firebase/auth';
import { db } from '../firebaseConfig';
import {
  collection,
  getDocs,
  doc,
  query,
  where,
  orderBy,
  deleteDoc,
} from 'firebase/firestore';

const LogoImage = styled('img')(({ theme }) => ({
  width: '230px',
  height: 'auto',
  [theme.breakpoints.down('sm')]: {
    width: '150px',
  },
}));

const MessageCard = ({ msg, onDelete }) => {
  const { t, i18n } = useTranslation();
  const isRtl = (i18n.resolvedLanguage || i18n.language || "").toLowerCase().startsWith("ar");
  const createdAt = useMemo(() => {
    try {
      if (!msg?.createdAt) return '—';
      const d =
        typeof msg.createdAt?.toDate === 'function'
          ? msg.createdAt.toDate()
          : new Date(msg.createdAt);
      const locale = i18n.resolvedLanguage || i18n.language || 'fr-FR';
      return new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(d);
    } catch {
      return '—';
    }
  }, [i18n.language, i18n.resolvedLanguage, msg?.createdAt]);

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: 2,
        p: 2,
        bgcolor: '#fff',
        direction: isRtl ? 'rtl' : 'ltr',
      }}
    >
      <Stack spacing={1.25}>
        {/* Header row: name + status chip + delete */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          sx={{ gap: 1 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontWeight: 800, color: '#12344d', fontSize: 18 }}>
              {msg.fullName || t("No name")}
            </Typography>
            <Chip
              label={(msg.status || "new") === "new" ? t("New") : t(msg.status)}
              size="small"
              color={(msg.status || 'new') === 'new' ? 'primary' : 'default'}
              sx={{ fontWeight: 700 }}
            />
          </Stack>

          <Tooltip title={t("Delete message")}>
            <IconButton
              aria-label="delete message"
              onClick={() => onDelete?.(msg)}
              sx={{
                marginInlineStart: 'auto',
                color: '#ef4444',
                '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' },
              }}
            >
              <DeleteOutlineRoundedIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Contact row */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ color: 'text.secondary', flexWrap: 'wrap' }}
        >
          {msg.email ? (
            <Tooltip title={t("Send email")}>
              <IconButton size="small" component={MuiLink} href={`mailto:${msg.email}`}>
                <MailOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          <Typography sx={{ marginInlineEnd: 0.5 }}>{msg.email || '—'}</Typography>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {msg.phone ? (
            <Tooltip title={t("Call")}>
              <IconButton size="small" component={MuiLink} href={`tel:${msg.phone}`}>
                <LocalPhoneRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          <Typography sx={{ marginInlineEnd: 0.5 }}>{msg.phone || '—'}</Typography>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <Stack direction="row" spacing={0.75} alignItems="center">
            <ScheduleRoundedIcon sx={{ fontSize: 18, color: '#64748b' }} />
            <Typography sx={{ color: '#64748b', fontSize: 13 }}>{createdAt}</Typography>
          </Stack>
        </Stack>

        {/* Message body */}
        <Box
          sx={{
            p: 1.25,
            bgcolor: '#F8FAFC',
            border: '1px solid #E5E7EB',
            borderRadius: 1.5,
            lineHeight: 1.9,
            color: '#0f172a',
          }}
        >
          {msg.message || '—'}
        </Box>
      </Stack>
    </Paper>
  );
};

function TeacherMessagesBox() {
  const { t, i18n } = useTranslation();
  const isRtl = (i18n.resolvedLanguage || i18n.language || "").toLowerCase().startsWith("ar");
  const auth = getAuth();
  const [loading, setLoading] = useState(true);
  const [teacherId, setTeacherId] = useState(null); // Firestore doc ID (NOT UID)
  const [messages, setMessages] = useState([]);
  const [err, setErr] = useState('');
  const [snack, setSnack] = useState({ open: false, type: 'success', text: '' });

  const fetchMessages = async (tId) => {
    const mQ = query(
      collection(db, 'contact_messages'),
      where('teacherId', '==', tId),
      orderBy('createdAt', 'desc')
    );
    const mSnap = await getDocs(mQ);
    const rows = mSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setMessages(rows);
  };

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      try {
        setLoading(true);
        setErr('');

        const user = auth.currentUser;
        if (!user) {
         setErr(t("You must be logged in to access messages."));
          return;
        }

        // احصل على ID وثيقة الأستاذ عبر الـ UID
        const tQ = query(collection(db, 'teachers'), where('uid', '==', user.uid));
        const tSnap = await getDocs(tQ);
        if (tSnap.empty) {
          setErr(t("Unable to find the linked teacher account."));
          return;
        }
        const tDoc = tSnap.docs[0];
        const tId = tDoc.id;
        if (!mounted) return;

        setTeacherId(tId);
        await fetchMessages(tId);
      } catch (e) {
        console.error(e);
        if (mounted) setErr(t("An error occurred while fetching messages."));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [auth]);

  const handleDelete = async (msg) => {
    try {
      const ok = window.confirm(t("Do you want to permanently delete this message?"));
      if (!ok) return;

      // حذف من فايرستور
      await deleteDoc(doc(db, 'contact_messages', msg.id));

      // تحديث محلي (Optimistic)
      setMessages((prev) => prev.filter((m) => m.id !== msg.id));

      setSnack({ open: true, type: "success", text: t("Message deleted successfully.") });
    } catch (e) {
      console.error(e);
      setSnack({ open: true, type: "error", text: t("Unable to delete the message.") });
    }
  };

  return (
    <div>
      {/* Header */}
      <Typography
        id="modal-title"
        variant="h5"
        component="h2"
        sx={{
          color: '#12344d',
          borderBottom: '1px solid #ddd',
          mb: 2,
          direction: isRtl ? 'rtl' : 'ltr',
          textAlign: isRtl ? 'right' : 'left',
          pb: 1,
          fontWeight: 800,
        }}
      >
        {t("Messages Inbox")}
      </Typography>

      {/* Loading */}
      {loading && (
        <Box sx={{ py: 6, display: 'grid', placeItems: 'center', color: '#12344d' }}>
          <CircularProgress />
          <Typography sx={{ mt: 1.5 }}>{t("Loading...")}</Typography>
        </Box>
      )}

      {/* Error */}
      {!loading && err && (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: 2,
            direction: isRtl ? 'rtl' : 'ltr',
            bgcolor: '#fff',
            borderColor: '#fecaca',
            backgroundImage:
              'linear-gradient(to right, rgba(254,226,226,.25), rgba(255,255,255,0))',
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <ErrorOutlineRoundedIcon sx={{ color: '#dc2626' }} />
            <Typography sx={{ color: '#991b1b' }}>{err}</Typography>
          </Stack>
        </Paper>
      )}

      {/* Empty */}
      {!loading && !err && messages.length === 0 && (
        <>
          <Typography
            id="modal-content"
            variant="body1"
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <LogoImage src={emptyBox} alt={t("Logo")} />
          </Typography>
          <Typography
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontFamily: 'Arial',
              fontSize: '18px',
              color: '#b0b0b0',
              textAlign: 'center',
              lineHeight: 1.5,
              my: 2,
            }}
          >
            {t("There are no conversations at the moment")}
          </Typography>
        </>
      )}

      {/* List */}
      {!loading && !err && messages.length > 0 && (
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {messages.map((m) => (
            <MessageCard key={m.id} msg={m} onDelete={handleDelete} />
          ))}
        </Stack>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.type === 'error' ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {snack.text}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default TeacherMessagesBox;
